import type { RecordingCaptureEvent, RecordingUploadTrack } from '@/shared/api/recording/types';
import type { SpaceCredits } from '@/shared/types/Space';

import { isPaidSubscription } from '@/shared/lib/billing/isPaidSubscription';
import { RecordingFreeMinutes } from '@/shared/lib/billing/trialRecording';

export const getRecordingDurationS = (recordedMs: number) => Math.max(0.1, recordedMs / 1000);

export const getUploadedTracks = (hasCamera: boolean): RecordingUploadTrack[] =>
  hasCamera ? ['recording', 'camera'] : ['recording'];

/** Places the camera bubble near the bottom-left corner of the screen recording from its first frame. */
export const getCaptureEvents = (hasCamera: boolean): RecordingCaptureEvent[] =>
  hasCamera
    ? [
        {
          payload: {
            position: { x: 0.03, y: 0.7 },
            shape: 'circle',
            size: { height: 0.25, width: 0.14 },
            t: 0,
            visible: true,
          },
          timestamp: 0,
          type: 'camera_layout',
        },
      ]
    : [];

const chargeFreeMinutes = (minutes: RecordingFreeMinutes, recordingDurationS: number): RecordingFreeMinutes => ({
  ...minutes,
  used: minutes.used + Math.max(1, Math.round(recordingDurationS / 60)),
});

/**
 * Charges a saved recording to the cached credits of the space it was made in (whole minutes, at least one, as the
 * backend does), so the space's free-recording paywall is up to date before the credits are refetched. Paid spaces, spaces whose recording is unlimited and spaces without free recording minutes are returned unchanged.
 */
export const chargeSpaceRecordingMinutes = (
  spaceCredits: SpaceCredits | undefined,
  recordingDurationS: number,
  isRecordingUnlimited = false,
): SpaceCredits | undefined => {
  const minutes = spaceCredits?.credits?.recording_free_minutes;
  if (!spaceCredits || !minutes || isRecordingUnlimited || isPaidSubscription(spaceCredits.subscription))
    return spaceCredits;

  return {
    ...spaceCredits,
    credits: { ...spaceCredits.credits, recording_free_minutes: chargeFreeMinutes(minutes, recordingDurationS) },
  };
};
